<!-- =========================================================================================
  File Name: ProjectEditTabInformation.vue
  Description: Project Edit Information Tab content
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/project/pixinvent
========================================================================================== -->

<template>
  <div id="project-edit-tab-info">
    <!-- Project Row -->
    <div class="vx-row">
      <div class="vx-col md:w-1/2 w-full">
        <div class="flex items-start flex-col sm:flex-row"></div>
      </div>
      <div class="vx-col md:w-1/2 w-full">
        <div class="flex items-start flex-col sm:flex-row"></div>
      </div>
    </div>

    <!-- Content Row -->
    <div class="vx-row">
      <div class="vx-col md:w-1/2 w-full">
        <vs-input
          class="w-full mt-4"
          label="Project name"
          v-model="data_local.project_name"
          v-validate="'required|alpha_num'"
          name="projectname"
        />
        <span class="text-danger text-sm" v-show="errors.has('projectname')">{{
          errors.first("projectname")
        }}</span>

        <vs-input
          class="w-full mt-4"
          label="Company"
          v-model="data_local.company"
          v-validate="'alpha_spaces'"
          name="company"
        />
        <span class="text-danger text-sm" v-show="errors.has('company')">{{
          errors.first("company")
        }}</span>
        <div class="mt-4">
          <label class="vs-input--label">Status</label>
          <v-select
            v-model="data_local.status"
            :clearable="false"
            :options="statusOptions"
            v-validate="'required'"
            name="status"
            :dir="$vs.rtl ? 'rtl' : 'ltr'"
          />
          <span class="text-danger text-sm" v-show="errors.has('status')">{{
            errors.first("status")
          }}</span>
        </div>
        <div class="mt-4">
          <label class="vs-input--label">Property type</label>
          <v-select
            v-model="data_local.property_type"
            :clearable="false"
            :options="propertyTypeOptions"
            v-validate="'required'"
            name="propertytype"
            :dir="$vs.rtl ? 'rtl' : 'ltr'"
          />
          <span
            class="text-danger text-sm"
            v-show="errors.has('propertytype')"
            >{{ errors.first("propertytype") }}</span
          >
        </div>
        <div class="mt-4">
          <vs-input
            class="w-full mt-4"
            label="Units Qty"
            v-model="data_local.units_qty"
            name="unitsQty"
          />
        </div>
        <div class="mt-4">
          <vs-input
            class="w-full mt-4"
            label="Gross area (m2)"
            v-model="data_local.gross_area"
            name="grossArea"
          />
        </div>
        <div class="mt-5">
          <label class="vs-input--label">Sales Price Range</label>

          <vs-row>
            <vs-col
              vs-type="flex"
              vs-justify="center"
              vs-align="center"
              vs-w="10"
            >
              <vs-slider
                color="danger"
                ticks
                step="10"
                text-fixed="€/m2"
                v-model="data_local.sales_price_range"
              />
            </vs-col>
            <vs-col
              vs-type="flex"
              vs-justify="center"
              vs-align="center"
              vs-w="2"
            >
              {{ data_local.sales_price_range[0] }} -
              {{ data_local.sales_price_range[1] }} €/m<sup>2</sup>
            </vs-col>
          </vs-row>
        </div>
      </div>

      <div class="vx-col md:w-1/2 w-full">
        <div>
          <div class="img-container mb-4">
            <img :src="data_local.project_logo" class="rounded" />
          </div>
          <!-- Image upload Buttons -->
          <div class="modify-img mt-5">
            <input
              type="file"
              class="hidden"
              ref="updateProjectLogoInput"
              @change="updateProjectLogo"
              accept="image/*"
            />
            <vs-button
              class="mr-4 mb-4"
              type="border"
              color="danger"
              @click="$refs.updateProjectLogoInput.click()"
              >Update Logo</vs-button
            >
            <vs-button @click="data_local.project_logo = null"
              >Remove Logo</vs-button
            >
          </div>
        </div>
        <div>
          <div class="img-container mb-4">
            <img :src="data_local.project_cover_img" class="rounded" />
          </div>
          <!-- Image upload Buttons -->
          <div class="modify-img mt-5">
            <input
              type="file"
              class="hidden"
              ref="updateProjectCoverImgInput"
              @change="updateProjectCoverImg"
              accept="image/*"
            />
            <vs-button
              class="mr-4 mb-4"
              type="border"
              color="danger"
              @click="$refs.updateProjectCoverImgInput.click()"
              >Update Cover Image</vs-button
            >
            <vs-button @click="data_local.project_cover_img = null"
              >Remove Cover Image</vs-button
            >
          </div>
        </div>
      </div>
    </div>

    <!-- Save & Reset Button -->
    <div class="vx-row">
      <div class="vx-col w-full">
        <div class="mt-8 flex flex-wrap items-center justify-end">
          <span>
            <ul class="con-s">
              <li></li>
              <li>
                <vs-switch color="success" v-model="data_local.is_verified">
                  <span slot="on">Verified</span>
                  <span slot="off">Non verified</span>
                </vs-switch>
              </li>
            </ul></span
          >
          <vs-button
            icon-pack="feather"
            icon="icon-save"
            type="border"
            color="danger"
            class="ml-auto mt-2"
            @click="save_changes"
            :disabled="!validateForm"
            >Save Changes</vs-button
          >
          <vs-button
            icon-pack="feather"
            icon="icon-refresh-cw"
            class="ml-4 mt-2"
            type="border"
            color="warning"
            @click="reset_data"
            >Reset</vs-button
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import vSelect from "vue-select";

export default {
  components: {
    vSelect
  },
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      data_local: JSON.parse(JSON.stringify(this.data)),

      statusOptions: [
        { label: "Active", value: "active" },
        { label: "Inactive", value: "inactive" },
        { label: "Archived", value: "archived" }
      ],
      propertyTypeOptions: [
        { label: "Office", value: "office" },
        { label: "Condominium", value: "condominium" }
      ]
    };
  },
  computed: {
    status_local: {
      get() {
        return {
          label: this.capitalize(this.data_local.status),
          value: this.data_local.status
        };
      },
      set(obj) {
        this.data_local.status = obj.value;
      }
    },
    role_local: {
      get() {
        return {
          label: this.capitalize(this.data_local.role),
          value: this.data_local.role
        };
      },
      set(obj) {
        this.data_local.role = obj.value;
      }
    },
    validateForm() {
      return !this.errors.any();
    }
  },
  methods: {
    capitalize(str) {
      return str.slice(0, 1).toUpperCase() + str.slice(1, str.length);
    },
    updateProjectLogo(input) {
      if (input.target.files && input.target.files[0]) {
        const reader = new FileReader();
        reader.onload = e => {
          this.data_local.project_logo = e.target.result;
        };
        reader.readAsDataURL(input.target.files[0]);
      }
    },
    updateProjectCoverImg(input) {
      if (input.target.files && input.target.files[0]) {
        const reader = new FileReader();
        reader.onload = e => {
          this.data_local.project_cover_img = e.target.result;
        };
        reader.readAsDataURL(input.target.files[0]);
      }
    },
    save_changes() {
      /* eslint-disable */
      if (!this.validateForm) return;

      // Here will go your API call for updating data
      // You can get data in "this.data_local"

      /* eslint-enable */
    },
    reset_data() {
      this.data_local = JSON.parse(JSON.stringify(this.data));
    },
    update_project_logo() {
      // You can update avatar Here
      // For reference you can check dataList example
      // We haven't integrated it here, because data isn't saved in DB
    }
  }
};
</script>
